import { flatMap } from 'lodash';
import { Message } from '../../classes/Message';
import { RefreshJobEventTypeEnum } from '../../enums/refresh-job-event-type.enum';
import { OrganisationTypeEnum } from '../../enums/organisation-types';
import { Component, HostListener, Input, OnInit } from '@angular/core';
import { IOverNightParkingPhotoTaskSteps, ITaskInfo } from '../../interfaces/task-info';
import { EventService, FileStorageService, TaskService } from '../../services';
import { MessageService } from 'primeng';
import { IJobScreen } from '../../interfaces/job/job';
import { INgxGalleryImage } from 'src/app/user-controls/ngx-gallery/ngx-gallery-image.model';
import moment from 'moment';

@Component({
  selector: 'carry-over-auth',
  templateUrl: './carry-over-auth.component.html',
  styleUrls: ['./carry-over-auth.component.scss'],
})
export class CarryOverAuthComponent implements OnInit {
  @Input('jobInfo') jobInfo: IJobScreen;
  @Input('organisationTypeId') organisationTypeId: number;
  @Input('organisationId') organisationId: number;

  carryOverTask: ITaskInfo;
  public showCarryOverTasks = null;
  public carryOverTasks: {}[] = [];
  public showCarryOver: {} = {};
  public carryOverComment = '';
  public showCarryOverDialog = false;
  public loadingPopupPhoto = false;
  public popupPhotoUrl: string;
  public isShownPopupPhoto = false;
  public allowClosePopupPhoto = false;
  //Whether to trigger the authorisation carry over
  public triggerCarryOver = false;
  //Overnight parking images have to be an array, but it's just 1 photo at a time
  public overnightImages: INgxGalleryImage[] = [];
  public showImage = false;
  public overnightGalleryOptions = [
    {
      lazyLoading: false,
      image: false,
      startIndex: 0,
      showPreview: true,
      previewZoom: true,
      preview: true,
      zoomInIcon: 'fa fa-plus',
      zoomOutIcon: 'fa fa-minus',
      rotateLeftIcon: 'fa fa-rotate-left',
      rotateRightIcon: 'fa fa-rotate-right',
      rotate: true,
      previewDescription: true,
    },
  ];
  private readonly date_format = 'YYYY-MM-DD HH:mm';

  constructor(
    private taskService: TaskService,
    private messageService: MessageService,
    private eventService: EventService,
    private fileStorageService: FileStorageService,
  ) {}

  ngOnInit(): void {
    this.initCarryOverTasks();
  }

  initCarryOverTasks() {
    this.carryOverTasks = flatMap(this.jobInfo.orders, (o) =>
      o.tasks.map((t) => ({ ...t, order: o })),
    );
    console.log('CARRY OVER TASKS ', this.carryOverTasks);
  }

  private getOverNightParkingPhotoDescriptionData(
    overnightParkingPhotoTaskStep: IOverNightParkingPhotoTaskSteps,
  ) {
    let areaTitle: string = '';
    const dateTime = `DateTime:${moment(overnightParkingPhotoTaskStep.dateTime).format(
      this.date_format,
    )}`;
    switch (overnightParkingPhotoTaskStep.overNightParkingPhotoType) {
      case 0:
        areaTitle = `Vehicle Parking - ${dateTime}`;
        break;
      case 1:
        areaTitle = `OverNight Odometer - ${dateTime}`;
        break;
      default:
        areaTitle = `Resume Odometer - ${dateTime}`;
        break;
    }

    return {
      areaTitle,
      dateTime: overnightParkingPhotoTaskStep.dateTime,
      isSignature: false,
    };
  }

  private async getPhotoUrl(
    overNightParkingPhotoTaskSteps: Array<IOverNightParkingPhotoTaskSteps>,
  ): Promise<void> {
    return new Promise<void>(async (resolve) => {
      for (const overnightParkingPhotoTaskStep of overNightParkingPhotoTaskSteps) {
        const url = await this.fileStorageService.getPreSignedURLs([
          overnightParkingPhotoTaskStep.photoLocation,
        ]);
        const extensions = overnightParkingPhotoTaskStep.photoLocation?.split('.');
        const extension = extensions[extensions.length - 1] === 'pdf' ? 'pdf' : 'jpg';
        this.overnightImages.push({
          url: url[0],
          extension,
          big: url[0],
          medium: url[0],
          small: url[0],
          key: overnightParkingPhotoTaskStep.photoLocation,
          descriptionData: this.getOverNightParkingPhotoDescriptionData(
            overnightParkingPhotoTaskStep,
          ),
        });
      }
      this.loadingPopupPhoto = false;
      this.showImage = true;
      resolve();
    });
  }

  private async setupOverNightParkingPhoto(
    overNightParkingPhotoTaskSteps: Array<IOverNightParkingPhotoTaskSteps>,
  ): Promise<void> {
    const promise = this.getPhotoUrl(overNightParkingPhotoTaskSteps);
    const promise2 = new Promise((resolve) => {
      setTimeout(resolve, 1000, () => {
        this.loadingPopupPhoto = false;
        this.showImage = true;
      });
    });

    try {
      await Promise.race([promise, promise2]);
    } catch (error) {
      this.onOverNightImagePreviewClose();
    }
  }

  changeCarryOver(data: any) {
    this.carryOverTask = data;
    this.carryOverComment = '';
    this.showCarryOverDialog = true;
  }

  saveCarryOver() {
    console.log('SAVING CARRY OVER NOTE');

    this.taskService
      .setCarryOver(
        this.carryOverTask.id,
        this.carryOverTask.carryOverAuthorised,
        this.carryOverComment,
      )
      .then((res) => {
        this.messageService.add(Message.Success('Carry over note saved'));
        this.triggerCarryOver = true;
        this.showCarryOverDialog = false;
        this.eventService.RefreshJobEvent.emit(RefreshJobEventTypeEnum.RefreshHistory);
      });
  }

  cancelCarryOver() {
    this.showCarryOverDialog = false;
  }

  hideCarryOver() {
    if (this.triggerCarryOver) {
      this.triggerCarryOver = false;
      return;
    }
    this.carryOverTask.carryOverAuthorised = !this.carryOverTask.carryOverAuthorised;
    this.showCarryOverDialog = false;
  }

  canChangeCarryOver(data: any) {
    return this.organisationTypeId != OrganisationTypeEnum.Engineius;
  }

  @HostListener('document:click', ['$event'])
  documentClick(event: MouseEvent) {
    if (this.allowClosePopupPhoto) {
      this.isShownPopupPhoto = false;
      this.loadingPopupPhoto = false;
      this.allowClosePopupPhoto = false;
    }
  }

  async openPhotoPresigned(
    overnightParkingPhotoTaskSteps: Array<IOverNightParkingPhotoTaskSteps>,
    index: number,
  ): Promise<void> {
    this.loadingPopupPhoto = true;
    await this.setupOverNightParkingPhoto(overnightParkingPhotoTaskSteps);

    if (this.overnightGalleryOptions.length > 0) {
      this.overnightGalleryOptions[0].startIndex = index;
    }
  }

  onOverNightImagePreviewClose() {
    this.overnightImages = [];
    this.showImage = false;
  }
}
